/**
 * Color utility functions for generating contrasting colors
 */

/**
 * Convert hex color to HSL values
 * @param {string} hex - Hex color string (e.g., "#FF0000")
 * @returns {Array} [hue, saturation, lightness] values
 */
export const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
};

/**
 * Convert HSL values to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generate contrasting colors based on a base color
 * @param {string} baseColor - Base hex color
 * @param {number} count - Number of colors to generate (default: 6)
 * @returns {Array} Array of hex color strings
 */
export const generateContrastingColors = (baseColor, count = 6) => {
  // Get base color HSL values
  const [baseHue] = hexToHsl(baseColor);
  const colors = [baseColor]; // Start with the client color

  // Define a set of contrasting hues that avoid the client color's hue family
  // These are strategically chosen to be visually distinct and work well on white backgrounds
  const contrastingHues = [
    0, // Red
    30, // Orange
    60, // Yellow
    120, // Green
    180, // Cyan
    240, // Blue
    270, // Purple
    300, // Magenta
    330, // Pink
  ];

  // Filter out hues that are too close to the client color's hue
  // We want to avoid colors within ±30 degrees of the client color
  const avoidHueRange = 30;
  const filteredHues = contrastingHues.filter((hue) => {
    const hueDiff = Math.abs(hue - baseHue);
    return hueDiff > avoidHueRange && hueDiff < 360 - avoidHueRange;
  });

  // If we don't have enough filtered hues, add some that are at least 60 degrees apart
  if (filteredHues.length < count - 1) {
    const additionalHues = contrastingHues.filter((hue) => {
      const hueDiff = Math.abs(hue - baseHue);
      return hueDiff > 60 && hueDiff < 300;
    });
    filteredHues.push(...additionalHues);
  }

  // Create contrasting colors with appropriate saturation and lightness for white backgrounds
  for (let i = 0; i < count - 1; i++) {
    const hue = filteredHues[i % filteredHues.length];

    // For white backgrounds, use medium to high saturation and medium to dark lightness
    // This ensures good contrast and readability
    let saturation, lightness;

    if (i < 3) {
      // First 3 colors: medium saturation, medium-dark lightness
      saturation = 60 + i * 10; // 60-80%
      lightness = 35 + i * 5; // 35-50%
    } else {
      // Remaining colors: higher saturation, varied lightness
      saturation = 70 + (i - 3) * 5; // 70-85%
      lightness = 30 + (i - 3) * 8; // 30-54%
    }

    // Ensure we don't exceed reasonable bounds
    saturation = Math.min(85, Math.max(50, saturation));
    lightness = Math.min(55, Math.max(25, lightness));

    colors.push(hslToHex(hue, saturation, lightness));
  }

  return colors.slice(0, count);
};
